import { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';

import useCommentsContext, { IComment } from './useCommentsContext';
import CommentItem from './CommentItem';

export default function CommentsListItems() {
  const { quillRef, comments, selectedCommentId } = useCommentsContext();

  const [ positions, setPositions ] = useState<Record<number, number>>({});

  const commentRefs = useRef<Record<number, HTMLDivElement>>({});

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => (a.anchor?.o || 0) - (b.anchor?.o || 0));
  }, [comments]);

  useEffect(() => {
    let mounted = true;
    const GAP = 8;

    const getItems = () => {
      const quill = quillRef.current?.getQuill();
      if (!quill) return [];

      return sortedComments.map((comment) => {
        const ref = commentRefs.current[comment.id];
        const height = ref?.offsetHeight || 0;
        if (!comment.anchor) return { comment, boundsTop: 0, height };
        const bounds = quill.getBounds(comment.anchor.o, comment.anchor.l);
        return { comment, boundsTop: bounds.top, height };
      });
    };

    const layoutItems = (
      items: ReturnType<typeof getItems>,
      selectedId: number | null
    ) => {
      const positions: Record<number, number> = {};
      const selIndex = items.findIndex((c) => c.comment.id === selectedId);

      if (selIndex === -1) {
        // No selection → forward pack
        let lastBottom = 0;
        for (const item of items) {
          const top = Math.max(item.boundsTop, lastBottom);
          positions[item.comment.id] = top;
          lastBottom = top + item.height + GAP;
        }
        return positions;
      }

      // Anchor selected
      const sel = items[selIndex];
      positions[sel.comment.id] = sel.boundsTop;

      // Layout downwards
      let lastBottom = sel.boundsTop + sel.height + GAP;
      for (let i = selIndex + 1; i < items.length; i++) {
        const it = items[i];
        const top = Math.max(it.boundsTop, lastBottom);
        positions[it.comment.id] = top;
        lastBottom = top + (it.height || 0) + GAP;
      }

      // Layout upwards: try to keep each comment at its anchor, but avoid overlap with the one below
      for (let i = selIndex - 1; i >= 0; i--) {
        const it = items[i];
        const below = items[i + 1];
        // Ideal position is its anchor
        let idealTop = it.boundsTop;
        // But can't overlap the one below
        const minTop = (positions[below.comment.id] || 0) - (it.height || 0) - GAP;
        const top = Math.min(idealTop, minTop);
        positions[it.comment.id] = top;
      }

      return positions;
    };

    const measure = () => {
      if (!mounted) return;

      const items = getItems();
      if (items.length === 0) return;

      const newPositions = layoutItems(items, selectedCommentId);
      if (mounted) setPositions(newPositions);
    };

    const raf1 = requestAnimationFrame(() => {
      measure();
      requestAnimationFrame(measure);
    });

    return () => {
      mounted = false;
      cancelAnimationFrame(raf1);
    };
  }, [quillRef, sortedComments, selectedCommentId]);

  if ( !sortedComments.length ) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}>
      {sortedComments.map((comment:IComment, index:number) => {
        const top = positions[comment.id] || 0;
        const isActive = selectedCommentId === comment.id;
        return (
          <Box
            key={comment.id}
            component="div"
            ref={(el: HTMLDivElement | undefined) => {
              if (el) commentRefs.current[comment.id] = el;
            }}
            sx={{
              position: 'absolute',
              top,
              width: '100%',
              zIndex: index + 1
            }}
          ><CommentItem comment={comment} isActive={isActive} /></Box>
        );
      })}
    </Box>
  )
};
